import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';

interface User {
  id: number;
  username: string;
  email: string;
  nickname: string;
  gender: string;
  age_group: string;
  phone: string;
  city: string;
  district: string;
  dong: string;
  interest_areas: string[];
  profile_image?: string | null;
  shipping_zip?: string;
  shipping_road_address?: string;
  shipping_detail?: string;
  date_joined: string;
  last_login: string;
  seller_profile?: number;
}

interface SellerImage {
  id: number;
  image: string | null;
  is_primary: boolean;
  order: number;
}

interface Seller {
  id: number;
  business_number: string;
  business_name: string;
  store_name?: string;
  business_address: string;
  category: string;
  phone: string;
  images: SellerImage[];
  is_approved: boolean;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  seller: Seller | null;
  token: string | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  sellerLogin: (businessNumber: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (userData: any, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [seller, setSeller] = useState<Seller | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem('token') || sessionStorage.getItem('token')
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // localStorage와 sessionStorage 모두 확인
      const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');
      const storedSeller = localStorage.getItem('seller') || sessionStorage.getItem('seller');
      
      // 토큰이 있으면 Authorization 헤더 설정
      if (storedToken) {
        api.defaults.headers.common['Authorization'] = `Token ${storedToken}`;
        setToken(storedToken);
        
        // 판매자가 저장되어 있으면 판매자 정보 설정 (사용자 정보와 분리)
        if (storedSeller) {
          try {
            const sellerData = JSON.parse(storedSeller);
            setSeller(sellerData);
            // 판매자는 사용자와 분리되므로 user 정보는 가져오지 않음
            setUser(null);
          } catch (error) {
            console.error('판매자 정보 파싱 실패:', error);
            localStorage.removeItem('seller');
            sessionStorage.removeItem('seller');
          }
        } else {
          // 판매자가 없으면 일반 사용자 정보 가져오기
          try {
            const response = await api.get('/users/profile/');
            setUser(response.data);
          } catch (error) {
            localStorage.removeItem('token');
            sessionStorage.removeItem('token');
            delete api.defaults.headers.common['Authorization'];
            setToken(null);
          }
        }
      } else if (storedSeller) {
        // 토큰은 없지만 판매자 정보만 있는 경우 (토큰이 만료되었을 수 있음)
        try {
          setSeller(JSON.parse(storedSeller));
        } catch (error) {
          localStorage.removeItem('seller');
          sessionStorage.removeItem('seller');
        }
      }
      
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      const response = await api.post('/users/login/', { email, password });
      const { user: userData, token: userToken } = response.data;
      
      // 자동로그인 여부에 따라 localStorage 또는 sessionStorage 사용
      if (rememberMe) {
        localStorage.setItem('token', userToken);
        // 기존 sessionStorage 정리
        sessionStorage.removeItem('token');
      } else {
        sessionStorage.setItem('token', userToken);
        // 기존 localStorage 정리
        localStorage.removeItem('token');
      }
      
      api.defaults.headers.common['Authorization'] = `Token ${userToken}`;
      
      setUser(userData);
      setToken(userToken);
      
      // 판매자 정보 초기화 (사용자 로그인 시)
      setSeller(null);
      localStorage.removeItem('seller');
      sessionStorage.removeItem('seller');
    } catch (error: any) {
      throw error;
    }
  };

  const sellerLogin = async (businessNumber: string, password: string, rememberMe: boolean = false) => {
    try {
      // 사업자번호에서 하이픈 제거
      const cleanBusinessNumber = businessNumber.replace(/-/g, '').trim();
      const response = await api.post('/sellers/login/', { 
        business_number: cleanBusinessNumber, 
        password 
      });
      const { seller: sellerData, token: sellerToken } = response.data;
      
      // 자동로그인 여부에 따라 localStorage 또는 sessionStorage 사용
      if (rememberMe) {
        localStorage.setItem('seller', JSON.stringify(sellerData));
        // 기존 sessionStorage 정리
        sessionStorage.removeItem('seller');
      } else {
        sessionStorage.setItem('seller', JSON.stringify(sellerData));
        // 기존 localStorage 정리
        localStorage.removeItem('seller');
      }
      
      setSeller(sellerData);
      
      // 판매자는 사용자와 분리되므로 user 정보 초기화
      setUser(null);
      
      // 토큰이 있으면 저장하고 Authorization 헤더 설정
      if (sellerToken) {
        if (rememberMe) {
          localStorage.setItem('token', sellerToken);
          sessionStorage.removeItem('token');
        } else {
          sessionStorage.setItem('token', sellerToken);
          localStorage.removeItem('token');
        }
        api.defaults.headers.common['Authorization'] = `Token ${sellerToken}`;
        setToken(sellerToken);
      } else {
        // 토큰이 없어도 판매자 로그인은 성공 (판매자 ID 헤더 기반 인증 사용)
        // 기존 토큰 제거
        setToken(null);
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
      }
    } catch (error: any) {
      // 에러 응답에 디버그 정보가 있으면 콘솔에 출력
      if (error.response?.data?.debug_info) {
        console.error('🔍 로그인 실패 디버그 정보:', error.response.data.debug_info);
      }
      throw error;
    }
  };

  const register = async (userData: any, rememberMe: boolean = false) => {
    try {
      const response = await api.post('/auth/register/', userData);
      const { user: newUser, token: userToken } = response.data;
      
      // 자동로그인 여부에 따라 localStorage 또는 sessionStorage 사용
      if (rememberMe) {
        localStorage.setItem('token', userToken);
        sessionStorage.removeItem('token');
      } else {
        sessionStorage.setItem('token', userToken);
        localStorage.removeItem('token');
      }
      
      api.defaults.headers.common['Authorization'] = `Token ${userToken}`;
      
      setUser(newUser);
      setToken(userToken);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '회원가입에 실패했습니다.');
    }
  };

  const logout = () => {
    // localStorage와 sessionStorage 모두 정리
    localStorage.removeItem('token');
    localStorage.removeItem('seller');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('seller');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setSeller(null);
    setToken(null);
  };

  const value = {
    user,
    seller,
    token,
    login,
    sellerLogin,
    register,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};